/**
 * API Client - Irrigo Platform
 * Cliente HTTP base para comunicación con el backend
 */

// Configuración de la API
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ||
  import.meta.env.VITE_API_URL ||
  'http://localhost:8080';
const API_VERSION = 'v1';

// Token storage keys
const TOKEN_KEY = 'irrigo_token';
const REFRESH_TOKEN_KEY = 'irrigo_refresh_token';

// ============================================================================
// TIPOS DE RESPUESTA
// ============================================================================

export interface ApiError {
  code: string;
  message: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: ApiError;
  message?: string;
}

export interface PaginationInfo {
  page: number;
  pageSize?: number;
  limit?: number;
  total: number;
  totalPages: number;
}

export interface PaginatedData<T> {
  items: T[];
  pagination: PaginationInfo;
}

export interface PaginatedResponse<T> extends ApiResponse<PaginatedData<T>> {}

// Parámetros de paginación para las peticiones
export interface PaginationParams {
  page?: number;
  pageSize?: number;
  search?: string;
}

// ============================================================================
// TOKEN MANAGEMENT
// ============================================================================

export const tokenManager = {
  getToken: (): string | null => {
    return localStorage.getItem(TOKEN_KEY);
  },

  setToken: (token: string): void => {
    localStorage.setItem(TOKEN_KEY, token);
  },

  removeToken: (): void => {
    localStorage.removeItem(TOKEN_KEY);
  },

  getRefreshToken: (): string | null => {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  },

  setRefreshToken: (token: string): void => {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  },

  removeRefreshToken: (): void => {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },

  clearAll: (): void => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },

  isAuthenticated: (): boolean => {
    return !!localStorage.getItem(TOKEN_KEY);
  },
};

// ============================================================================
// API CLIENT
// ============================================================================

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

interface RequestOptions {
  method?: HttpMethod;
  body?: unknown;
  headers?: Record<string, string>;
  skipAuth?: boolean;
  params?: Record<string, string | number | boolean | undefined>;
}

class ApiClient {
  private baseUrl: string;
  private isRefreshing: boolean = false;
  private refreshPromise: Promise<string | null> | null = null;

  constructor() {
    this.baseUrl = `${API_BASE_URL}/${API_VERSION}`;
  }

  /**
   * Construye la URL con query params
   */
  private buildUrl(endpoint: string, params?: Record<string, string | number | boolean | undefined>): string {
    const url = new URL(`${this.baseUrl}${endpoint}`);
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          url.searchParams.append(key, String(value));
        }
      });
    }
    
    return url.toString();
  }

  /**
   * Construye los headers de la petición
   */
  private buildHeaders(options: RequestOptions): Headers {
    const headers = new Headers({
      'Content-Type': 'application/json',
      ...options.headers,
    });

    // Añadir token de autenticación si existe y no se omite
    if (!options.skipAuth) {
      const token = tokenManager.getToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
    }

    return headers;
  }

  /**
   * Intenta refrescar el token
   */
  private async refreshToken(): Promise<string | null> {
    // Si ya estamos refrescando, esperar a esa promesa
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.refreshPromise = this._doRefresh();

    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  private async _doRefresh(): Promise<string | null> {
    const currentToken = tokenManager.getToken();
    
    if (!currentToken) {
      return null;
    }

    try {
      const response = await fetch(`${this.baseUrl}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`,
        },
      });

      if (!response.ok) {
        tokenManager.clearAll();
        return null;
      }

      const data = await response.json();
      
      if (data.success && data.data?.token) {
        tokenManager.setToken(data.data.token);
        return data.data.token;
      }

      return null;
    } catch {
      tokenManager.clearAll();
      return null;
    }
  }

  /**
   * Realiza la petición HTTP
   */
  private async request<T>(endpoint: string, options: RequestOptions = {}): Promise<ApiResponse<T>> {
    const { method = 'GET', body, params, skipAuth } = options;
    
    const url = this.buildUrl(endpoint, params);
    const headers = this.buildHeaders(options);

    const fetchOptions: RequestInit = {
      method,
      headers,
    };

    if (body && method !== 'GET') {
      fetchOptions.body = JSON.stringify(body);
    }

    try {
      let response = await fetch(url, fetchOptions);

      // Si es 401 y no estamos omitiendo auth, intentar refrescar token
      if (response.status === 401 && !skipAuth) {
        const newToken = await this.refreshToken();
        
        if (newToken) {
          // Reintentar la petición con el nuevo token
          headers.set('Authorization', `Bearer ${newToken}`);
          fetchOptions.headers = headers;
          response = await fetch(url, fetchOptions);
        } else {
          // No se pudo refrescar, lanzar error de autenticación
          return {
            success: false,
            error: {
              code: 'UNAUTHORIZED',
              message: 'Session expired. Please login again.',
            },
          };
        }
      }

      // Parsear cuerpo de forma segura (evita romper UI con HTML/texto no-JSON)
      const rawText = await response.text();
      const trimmed = rawText.trim();

      if (!trimmed) {
        if (!response.ok) {
          return {
            success: false,
            error: {
              code: `HTTP_${response.status}`,
              message: response.statusText || `Error HTTP ${response.status}`,
            },
          };
        }
        return {
          success: false,
          error: {
            code: 'EMPTY_RESPONSE',
            message: 'El servidor devolvió una respuesta vacía',
          },
        };
      }

      let data: unknown;
      try {
        data = JSON.parse(trimmed);
      } catch {
        const snippet = trimmed.slice(0, 80).replace(/\s+/g, ' ');
        return {
          success: false,
          error: {
            code: response.status === 404 ? 'HTTP_404' : 'INVALID_JSON',
            message: !response.ok
              ? `Error HTTP ${response.status}: respuesta no JSON`
              : `Respuesta del servidor no es JSON válido (${snippet})`,
          },
        };
      }

      // Si la respuesta no es exitosa
      if (!response.ok) {
        const errBody = data as { error?: ApiError; message?: string };
        return {
          success: false,
          error: errBody.error || {
            code: `HTTP_${response.status}`,
            message: errBody.message || response.statusText || `Error HTTP ${response.status}`,
          },
        };
      }

      return data as ApiResponse<T>;
    } catch (error) {
      console.error('API Request Error:', error);

      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error occurred',
        },
      };
    }
  }

  // ============================================================================
  // HTTP METHODS
  // ============================================================================

  /**
   * GET request
   */
  async get<T>(endpoint: string, params?: Record<string, string | number | boolean | undefined>): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET', params });
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'POST', body });
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'PUT', body });
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  /**
   * PATCH request
   */
  async patch<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'PATCH', body });
  }

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  /**
   * GET paginado
   * Convierte pageSize a limit para compatibilidad con el backend
   * Añade page=1 por defecto si no se especifica
   */
  async getPaginated<T>(
    endpoint: string,
    params?: PaginationParams & Record<string, string | number | boolean | undefined>
  ): Promise<PaginatedResponse<T>> {
    // Convertir pageSize a limit (el backend usa 'limit', no 'pageSize')
    const backendParams = params ? { ...params } : {};
    if (backendParams.pageSize !== undefined) {
      backendParams.limit = backendParams.pageSize;
      delete backendParams.pageSize;
    }
    // Añadir page=1 por defecto si no se especifica (algunos endpoints lo requieren)
    if (backendParams.page === undefined) {
      backendParams.page = 1;
    }
    return this.get<PaginatedData<T>>(endpoint, backendParams);
  }

  /**
   * GET que puede retornar array directo o paginado
   * Útil para endpoints que no siempre paginan (zones, servers)
   */
  async getList<T>(endpoint: string, params?: Record<string, string | number | boolean | undefined>): Promise<ApiResponse<T[]>> {
    const response = await this.request<T[] | PaginatedData<T>>(endpoint, { method: 'GET', params });
    
    if (!response.success) {
      return response as ApiResponse<T[]>;
    }

    // Si la respuesta es un array directo, devolverlo
    if (Array.isArray(response.data)) {
      return { success: true, data: response.data };
    }

    // Si es paginado, extraer los items
    if (response.data && 'items' in response.data) {
      return { success: true, data: response.data.items };
    }

    // Fallback: devolver como está
    return response as ApiResponse<T[]>;
  }

  /**
   * Request sin autenticación (para login)
   */
  async publicPost<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'POST', body, skipAuth: true });
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<{ status: string; timestamp: string; version: string } | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/health`);
      if (response.ok) {
        return response.json();
      }
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Obtener URL base de la API
   */
  getBaseUrl(): string {
    return this.baseUrl;
  }
}

// Exportar instancia singleton
export const api = new ApiClient();

// Export default
export default api;
